import {
  clearUserData,
  CommonEventProperties,
  disableAnonymousTracking,
  enableAnonymousTracking,
  newTracker,
  PageViewEvent,
  SelfDescribingEvent,
  setUserId,
  StructuredEvent,
  trackPageView,
  trackSelfDescribingEvent,
  trackStructEvent,
  setCustomUrl,
} from '@snowplow/browser-tracker';

import {
  FunnelContext,
  PlayContext,
  SpaceContext,
  TriggerContext,
  UserContext,
  WorkspaceContext,
  WorkspaceMemberContext,
} from '@genially/snowplow-contracts';

import { VWOPlugin } from './vendor/vwo-snowplow-browser-tracker/VWOPlugin';

import { mapAllContextsAsArray } from './utils/mapAllContextsAsArray';

export interface SnowplowCookieHandler {
  areSnowplowCookiesEnabled(): boolean | Error;
}

export class SnowplowWrapper {
  private readonly cookieHandler: SnowplowCookieHandler;
  private readonly collectorUrl: string;
  private readonly appId: string;
  private readonly trackerName: string;
  private readonly isProduction: boolean;
  private readonly debug: boolean;
  private userId?: string;

  public constructor(
    trackerName: string,
    isProduction: boolean,
    cookieHandler: SnowplowCookieHandler,
    collectorUrl: string,
    appId: string,
    debug?: boolean,
  ) {
    this.trackerName = trackerName;
    this.cookieHandler = cookieHandler;
    this.isProduction = isProduction;
    this.collectorUrl = collectorUrl;
    this.appId = appId;
    this.debug = !!debug;
  }

  public setUserId = (userId: string): void => {
    this.userId = userId;
  };

  public isProductionCollector = (): boolean => this.isProduction;

  public initTracker = ({ userId }: { userId?: string }): void => {
    if (!this.collectorUrl) {
      console.log('The Snowplow collectorUrl is not configured');

      return;
    }

    if (this.debug) {
      console.debug('[Snowplow] Init tracker');

      return;
    }

    if (this.cookieHandler.areSnowplowCookiesEnabled()) {
      newTracker(this.trackerName, this.collectorUrl, {
        appId: this.appId,
        plugins: [VWOPlugin()],
        stateStorageStrategy: 'cookieAndLocalStorage',
        discoverRootDomain: true,
      });
    } else {
      newTracker(this.trackerName, this.collectorUrl, {
        appId: this.appId,
        plugins: [VWOPlugin()],
        anonymousTracking: { withSessionTracking: true, withServerAnonymisation: true },
        stateStorageStrategy: 'cookieAndLocalStorage',
        discoverRootDomain: true,
      });
    }

    if (userId) {
      this.setUserId(userId);
    }

    setUserId(userId, [this.trackerName]);
  };

  private readonly updateTrackConfig = (): void => {
    if (this.debug) {
      console.debug('[Snowplow] changing tracker configuration');

      return;
    }

    if (this.cookieHandler.areSnowplowCookiesEnabled()) {
      this.trackWithCookies();
    } else {
      this.trackWithoutCookies();
    }
  };

  private readonly trackWithoutCookies = (): void => {
    enableAnonymousTracking({
      options: { withSessionTracking: true, withServerAnonymisation: true },
      stateStorageStrategy: 'cookieAndLocalStorage',
    });
    clearUserData({ preserveSession: true, preserveUser: true });
  };

  private readonly trackWithCookies = (): void => {
    disableAnonymousTracking({ stateStorageStrategy: 'cookieAndLocalStorage' });
    setUserId(this.userId);
  };

  public trackEvent = (event: SelfDescribingEvent & CommonEventProperties): void => {
    this.updateTrackConfig();

    if (this.debug) {
      console.debug('[Snowplow] Sending Self describing event: ', event);
    } else {
      trackSelfDescribingEvent(event, [this.trackerName]);
    }
  };

  public trackStructuredEvent = (
    event: StructuredEvent & CommonEventProperties,
  ): void => {
    this.updateTrackConfig();

    if (this.debug) {
      console.debug('[Snowplow] Sending Structured event: ', event);
    } else {
      trackStructEvent(event, [this.trackerName]);
    }
  };

  private readonly trackPageView = (
    event?: (PageViewEvent & CommonEventProperties) | undefined,
  ): void => {
    this.updateTrackConfig();

    if (this.debug) {
      console.debug('[Snowplow] Sending Page View event: ', event);
    } else {
      trackPageView(event);
    }
  };

  public sendPageViewEvent = ({
    user,
    team,
    teamMember,
    space,
    play,
    trigger,
    funnel,
  }: {
    user?: UserContext;
    team?: WorkspaceContext;
    teamMember?: WorkspaceMemberContext;
    space?: SpaceContext;
    play?: PlayContext;
    trigger?: TriggerContext;
    funnel?: FunnelContext;
  }): void => {
    this.trackPageView({
      context: mapAllContextsAsArray({
        user,
        team,
        teamMember,
        space,
        play,
        trigger,
        funnel,
      }),
    });
  };

  public setCustomUrl = (url: string): void => {
    setCustomUrl(url);
  };
}
